import {
  MutationFunction,
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query'

import { fetchOnClient } from '@/utils/fetcher/fetcher.client'

import { ConfigCacheOneDay } from '@/configs/constants'

import {
  DistrictAncestorsResponse,
  SatpenListResponse,
  YayasanListResponse,
  YayasanStatsByParentWilayahResponse,
} from './types'
import { ENDPOINT, fetchGetBlob } from '../utils/fetcher/fetcher.helpers'

export const useFetchYayasanStatsByParentWilayah = (
  parentWilayah: string,
  options: {
    enabled: boolean
  }
): UseQueryResult<YayasanStatsByParentWilayahResponse, Error> => {
  return useQuery<YayasanStatsByParentWilayahResponse, Error>({
    queryKey: [`yayasan-descendants`, parentWilayah],
    queryFn: async (): Promise<YayasanStatsByParentWilayahResponse> =>
      fetchOnClient<YayasanStatsByParentWilayahResponse>(
        `${ENDPOINT.DATA_INDUK_YAYASAN}/statistics/${parentWilayah}/descendants`
      ),
    ...ConfigCacheOneDay,
    ...{
      enabled: options.enabled,
    },
  })
}

export const useWilayahBreadcrumbs = (
  kodeWilayah: string,
  options: {
    enabled: boolean
  }
): UseQueryResult<DistrictAncestorsResponse, Error> => {
  return useQuery<DistrictAncestorsResponse, Error>({
    queryKey: [`yayasan-breadcrumbs`, kodeWilayah],
    queryFn: async (): Promise<DistrictAncestorsResponse> =>
      fetchOnClient<DistrictAncestorsResponse>(
        `${ENDPOINT.DATA_INDUK_YAYASAN}/districts/${kodeWilayah}/ancestors`
      ),
    ...ConfigCacheOneDay,
    ...{
      enabled: options.enabled,
    },
  })
}

interface YayasanFiltersWithKeywordParams {
  keyword: string
  kodeKecamatan: string
  sortBy: string
  sortDir: string
  jenis: string
}

export interface YayasanDownloadParams extends YayasanFiltersWithKeywordParams {
  format?: 'csv' | 'xlsx' | 'pdf'
}

export function generateDataIndukYayasanQueryString(
  filters: YayasanFiltersWithKeywordParams
) {
  const searchParams = new URLSearchParams()
  if (filters?.kodeKecamatan) {
    searchParams.set('kodeKecamatan', filters?.kodeKecamatan)
  }
  if (filters?.keyword) {
    searchParams.set('keyword', filters?.keyword)
  }
  if (filters?.jenis) {
    searchParams.set('jenisYayasan', filters?.jenis)
  }
  if (filters?.sortBy) {
    searchParams.set('sortBy', filters?.sortBy)
  } else {
    searchParams.set('sortBy', 'nama_desa')
  }
  if (filters?.sortDir) {
    searchParams.set('sortDir', filters?.sortDir)
  } else {
    searchParams.set('sortDir', 'asc')
  }

  const str = searchParams.toString()
  const qs = str ? `&${str}` : ''
  return qs
}

export const useFetchSearchYayasan = (
  offset: number = 0,
  filters: YayasanFiltersWithKeywordParams,
  options: {
    enabled: boolean
  }
): UseQueryResult<YayasanListResponse, Error> => {
  const qs = generateDataIndukYayasanQueryString(filters)

  const fullQs = `?limit=20&offset=${offset}${qs}`

  return useQuery<YayasanListResponse, Error>({
    queryKey: [`yayasan-search`, offset, filters],
    queryFn: async (): Promise<YayasanListResponse> =>
      fetchOnClient<YayasanListResponse>(
        `${ENDPOINT.DATA_INDUK_YAYASAN}/search${fullQs}`
      ),
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 0,
    ...{
      enabled: options.enabled,
    },
  })
}

export const useFetchSekolahNaungan = (
  offset: number = 0,
  yayasanId: string,
  options: {
    enabled: boolean
  }
): UseQueryResult<SatpenListResponse, Error> => {
  return useQuery<SatpenListResponse, Error>({
    queryKey: [`yayasan-sekolah-naungan`, yayasanId, offset],
    queryFn: async (): Promise<SatpenListResponse> =>
      fetchOnClient<SatpenListResponse>(
        `${ENDPOINT.DATA_INDUK_YAYASAN}/${yayasanId}/satuan-pendidikan?limit=20&offset=${offset}`
      ),
    ...ConfigCacheOneDay,
    ...{
      enabled: options.enabled,
    },
  })
}

export const useMutateDownloadYayasan = (): UseMutationResult<
  Blob,
  Error,
  YayasanDownloadParams
> => {
  const mutateFn: MutationFunction<Blob, YayasanDownloadParams> = (
    filters: YayasanDownloadParams
  ): Promise<Blob> => {
    const qs = generateDataIndukYayasanQueryString(filters)

    return fetchGetBlob(
      `${ENDPOINT.DATA_INDUK_YAYASAN}/download?format=${filters.format || 'csv'}${qs}`
    )
  }

  return useMutation<Blob, Error, YayasanDownloadParams>({
    mutationKey: ['yayasan-download'],
    mutationFn: mutateFn,
  })
}
